/**
 * Glimmery offer state, see snippets/g-offer.liquid and docs/OFFERS.md.
 *
 * 1. State: asks Shopify (Storefront API, tokenless) through throwaway carts that are never shown or checked out:
 *    - giveaway: 1 tee + giveaway codes + a US address → is the tee free AND is shipping free?  → state A
 *    - launch:   2 tees + launch code + a US address  → does the launch code take money off?   → state B
 *      (the same cart tells whether "free shipping on 2+ tees" really applies → html.g-ship2)
 *    - neither                                                                                  → state C
 *    The answer is kept for 2 minutes in sessionStorage (this tab only). No answer → no promo class at all.
 *    v5: the same run quotes 1 … bundleMax tees (with the current state's code) to a US address, so the offer box
 *    can show the real shipping cost per quantity ("+ $5.00 US shipping" / "Save $7.40") and the real totals.
 * 1b. Countdown (v5): cfg.endsAt (Theme settings → Launch offer → End date, equal to the FIRST5 end date in
 *    Shopify) drives [data-g-countdown]. At zero the countdown hides (html.g-offer-expired) and Shopify is asked
 *    again, so the page switches to whatever really applies.
 * 2. Bag: the visitor's cart carries exactly the codes of the current state (other codes stay). If a code the bag
 *    had is gone because the offer was used up, the bag says so (html.g-notice-claimed / g-notice-ended) instead
 *    of silently showing a higher total.
 * 3. Checkout: every checkout submit re-asks Shopify first. Offer changed → bag updated, notice shown, no redirect;
 *    the shopper reviews the new total and presses Checkout again.
 * 4. Shipping in the bag: the real bag (lines + codes) is priced once more with a US address; only if shipping
 *    then costs nothing does html.g-cart-ship-free reveal "Free standard US shipping included."
 */
(() => {
  const cfg = window.GlimmeryOffer;
  if (!cfg || !cfg.variant) return;

  const root = document.documentElement;
  const STATE_KEY = 'g-offer:state';
  const NOTICE_KEY = 'g-offer:notice';
  const TTL = 2 * 60 * 1000;
  const US_ADDRESS = { address1: '350 5th Ave', city: 'New York', provinceCode: 'NY', zip: '10118', countryCode: 'US' };
  const up = (code) => String(code || '').toUpperCase();
  const giveCodes = cfg.giveaway ? [cfg.giveaway.code, cfg.giveaway.shipping] : [];
  const launchCodes = cfg.launch ? [cfg.launch.code] : [];
  // Codes the theme ever applied (incl. the ended giveaway): removed from a bag whenever they are not part of the
  // current state, so an old bag never carries a dead or unintended promotion into checkout.
  const legacyCodes = Array.isArray(cfg.legacyCodes) ? cfg.legacyCodes : [];
  const ours = new Set([...giveCodes, ...launchCodes, ...legacyCodes].map(up));
  const configKey = JSON.stringify([cfg.giveaway, cfg.launch]);
  const isUS = cfg.country === 'US';
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';

  const QUERY = `mutation GlimmeryOfferCheck($lines: [CartLineInput!], $codes: [String!], $country: CountryCode,
    $addresses: [CartSelectableAddressInput!]) @inContext(country: $country) {
    cartCreate(input: { lines: $lines, discountCodes: $codes, buyerIdentity: { countryCode: $country },
      delivery: { addresses: $addresses } }) {
      cart {
        cost { totalAmount { amount } }
        discountCodes { code applicable }
        discountAllocations { discountedAmount { amount } discountApplication { targetType }
          ... on CartCodeDiscountAllocation { code } }
        lines(first: 50) { nodes { cost { subtotalAmount { amount } }
          discountAllocations { discountedAmount { amount } discountApplication { targetType }
            ... on CartCodeDiscountAllocation { code } } } }
        deliveryGroups(first: 5) { nodes { selectedDeliveryOption { estimatedCost { amount } } } }
      }
    }
  }`;

  // Prices a throwaway cart. Returns null when Shopify gives no usable answer.
  async function simulate(lines, codes, country, withAddress) {
    const response = await fetch(`/api/${cfg.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          lines,
          codes,
          country: country || null,
          addresses: withAddress ? [{ selected: true, address: { deliveryAddress: US_ADDRESS } }] : [],
        },
      }),
    });
    if (!response.ok) return null;
    const cart = (await response.json())?.data?.cartCreate?.cart;
    if (!cart) return null;

    const num = (money) => parseFloat(money?.amount || '0') || 0;
    const applicable = {};
    cart.discountCodes.forEach((item) => (applicable[up(item.code)] = item.applicable));
    const byCode = {};
    let shipDiscount = 0;
    const allocations = [...cart.discountAllocations, ...cart.lines.nodes.flatMap((line) => line.discountAllocations)];
    allocations.forEach((item) => {
      const amount = num(item.discountedAmount);
      if (item.discountApplication?.targetType === 'SHIPPING_LINE') shipDiscount += amount;
      else if (item.code) byCode[up(item.code)] = (byCode[up(item.code)] || 0) + amount;
    });
    const groups = cart.deliveryGroups.nodes;
    const shipCost = groups.reduce((sum, group) => sum + num(group.selectedDeliveryOption?.estimatedCost), 0);
    return {
      applicable,
      byCode,
      items: cart.lines.nodes.reduce((sum, line) => sum + num(line.cost.subtotalAmount), 0),
      shipKnown: withAddress && groups.length > 0 && groups.every((group) => group.selectedDeliveryOption),
      shipCost,
      shipFree: shipCost > 0 && shipDiscount >= shipCost - 0.005,
      total: num(cart.cost.totalAmount),
    };
  }

  const tee = (quantity) => [{ merchandiseId: `gid://shopify/ProductVariant/${cfg.variant}`, quantity }];

  async function askShopify() {
    let giveaway = false;
    let shipOne = 0;
    if (cfg.giveaway) {
      const result = await simulate(tee(1), giveCodes, 'US', true);
      if (!result) return null;
      if (result.shipKnown) shipOne = result.shipCost;
      giveaway =
        result.applicable[up(cfg.giveaway.code)] === true &&
        (result.byCode[up(cfg.giveaway.code)] || 0) >= result.items - 0.005 &&
        result.shipKnown &&
        result.shipFree;
    }
    let launch = false;
    let ship2 = false;
    if (!giveaway || !isUS) {
      const result = await simulate(tee(2), launchCodes, 'US', true);
      if (!result) return null;
      launch = Boolean(cfg.launch) && result.applicable[up(cfg.launch.code)] === true && (result.byCode[up(cfg.launch.code)] || 0) > 0;
      ship2 = Boolean(cfg.shipping) && result.shipKnown && result.shipFree;
    }
    // Standard US shipping for one tee (product page line "Standard US shipping: $5.00 for one tee.").
    if (isUS && !shipOne && !giveaway) {
      const result = await simulate(tee(1), [], 'US', true);
      if (result && result.shipKnown) shipOne = result.shipCost;
    }
    const state = giveaway ? 'a' : launch ? 'b' : 'c';
    // The giveaway includes US shipping and is limited to the US market: other visitors see the regular shop.
    const view = state === 'a' && !isUS ? 'c' : state;
    // v5: real quotes per quantity for the offer box (US address; the codes of the state that applies).
    const quotes = {};
    if (isUS && view !== 'a') {
      const max = Math.max(1, Math.min(3, parseInt(cfg.bundleMax, 10) || 3));
      const codes = view === 'b' ? launchCodes : [];
      const results = await Promise.all(
        Array.from({ length: max }, (_, index) => simulate(tee(index + 1), codes, 'US', true).catch(() => null))
      );
      results.forEach((result, index) => {
        if (!result) return;
        quotes[index + 1] = {
          items: result.items,
          // Items after the code discount: total minus the shipping that is actually charged (taxes are included
          // in the shop's prices, so the total carries no extra tax line).
          itemsAfter: Math.max(0, result.total - (result.shipKnown && !result.shipFree ? result.shipCost : 0)),
          total: result.total,
          shipKnown: result.shipKnown,
          shipCost: result.shipCost,
          shipFree: result.shipFree,
        };
      });
      if (!shipOne && quotes[1] && quotes[1].shipKnown) shipOne = quotes[1].shipCost;
    }
    return { state, view, ship2: view !== 'a' && ship2, shipOne, quotes, t: Date.now(), key: configKey };
  }

  function readState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY));
      if (saved && saved.key === configKey && Date.now() - saved.t < TTL) return saved;
    } catch (e) {}
    return null;
  }

  let pending = null;
  // fresh = true skips the 2-minute memory (checkout, stale bag codes).
  function resolveState(fresh) {
    if (!fresh) {
      const saved = readState();
      if (saved) return Promise.resolve(saved);
    }
    if (pending) return pending;
    pending = askShopify()
      .catch(() => null)
      .then((result) => {
        pending = null;
        if (result) {
          try {
            sessionStorage.setItem(STATE_KEY, JSON.stringify(result));
          } catch (e) {}
        }
        return result;
      });
    return pending;
  }

  let current = null;
  function apply(result) {
    current = result;
    root.classList.remove('g-offer-a', 'g-offer-b', 'g-offer-c', 'g-ship2');
    fillShipOne();
    fillQuotes();
    if (!result) return;
    root.classList.add(`g-offer-${result.view}`);
    if (result.ship2) root.classList.add('g-ship2');
    document.dispatchEvent(new CustomEvent('glimmery:offer', { detail: result }));
  }

  const fmt = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD' }).format(amount);

  // Offer box (snippets/g-offer-box.liquid): per card the real shipping line and, in state B, the real total.
  // [data-g-ship-quote="n"]  "+ $5.00 US shipping"  only when the quote says shipping is NOT free for n tees
  // [data-g-ship-save="n"]   "Save $7.40"            only when shipping IS free for n tees (the amount saved)
  // [data-g-bundle-total="n"] the items total Shopify computed for n tees with the current code
  function fillQuotes() {
    const quotes = (current && isUS && current.view !== 'a' && current.quotes) || {};
    document.querySelectorAll('[data-g-ship-quote]').forEach((item) => {
      const quote = quotes[item.dataset.gShipQuote];
      const show = Boolean(quote && quote.shipKnown && !quote.shipFree && quote.shipCost > 0);
      const slot = item.querySelector('[data-g-ship-cost]');
      if (slot && show) slot.textContent = fmt(quote.shipCost);
      item.hidden = !show;
    });
    document.querySelectorAll('[data-g-ship-save]').forEach((item) => {
      const quote = quotes[item.dataset.gShipSave];
      const show = Boolean(quote && quote.shipKnown && quote.shipFree && quote.shipCost > 0);
      const slot = item.querySelector('[data-g-ship-cost]');
      if (slot && show) slot.textContent = fmt(quote.shipCost);
      item.hidden = !show;
    });
    document.querySelectorAll('[data-g-bundle-total]').forEach((item) => {
      const quote = quotes[item.dataset.gBundleTotal];
      if (quote && quote.itemsAfter > 0 && current.view === 'b') {
        const text = fmt(quote.itemsAfter);
        if (item.textContent !== text) item.textContent = text;
      }
    });
  }

  // ---------- countdown (v5) ----------
  const endsAt = cfg.endsAt ? Date.parse(cfg.endsAt) : NaN;
  let expiredHandled = false;
  // Reduced motion: the seconds unit is hidden and only the minute changes (WCAG 2.2.2: no content auto-updating every second).
  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = (n) => String(n).padStart(2, '0');
  function tickCountdown() {
    if (!Number.isFinite(endsAt)) return;
    const diff = endsAt - Date.now();
    const nodes = document.querySelectorAll('[data-g-countdown]');
    if (diff <= 0) {
      root.classList.add('g-offer-expired');
      nodes.forEach((node) => (node.hidden = true));
      if (!expiredHandled) {
        expiredHandled = true;
        // The offer is over by the clock: ask Shopify what applies now (the code's own end date decides).
        resolveState(true)
          .then((result) => {
            apply(result);
            return syncCart(result);
          })
          .catch(() => {});
      }
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const sec = Math.floor((diff % 60000) / 1000);
    const text = String(cfg.timeLeft || '[d] days, [h] hours, [m] minutes left')
      .replace('[d]', d)
      .replace('[h]', h)
      .replace('[m]', m);
    nodes.forEach((node) => {
      const set = (key, value) => {
        const el = node.querySelector(`[data-g-cd="${key}"]`);
        if (el && el.textContent !== value) el.textContent = value;
      };
      set('d', String(d));
      set('h', pad(h));
      set('m', pad(m));
      const secondsUnit = node.querySelector('[data-g-cd="s"]');
      if (reduceMotion()) {
        if (secondsUnit && secondsUnit.parentElement) secondsUnit.parentElement.hidden = true;
      } else {
        if (secondsUnit && secondsUnit.parentElement) secondsUnit.parentElement.hidden = false;
        set('s', pad(sec));
      }
      const sr = node.querySelector('[data-g-cd-text]');
      if (sr && sec === 0 && sr.textContent !== text) sr.textContent = text;
      if (sr && !sr.textContent) sr.textContent = text;
      node.hidden = false;
    });
  }
  if (Number.isFinite(endsAt)) {
    tickCountdown();
    setInterval(tickCountdown, 1000);
  }

  // "Standard US shipping: $5.00 for one tee." Only with a real quote, never during the giveaway (shipping is free
  // then). Product sections are re-rendered on Front/Back swaps, so new copies are filled as they appear.
  function fillShipOne() {
    const ok = current && isUS && current.view !== 'a' && current.shipOne > 0;
    const text = ok ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(current.shipOne) : '';
    document.querySelectorAll('[data-g-ship-one]').forEach((item) => {
      const slot = item.querySelector('[data-g-ship-one-cost]');
      if (slot && slot.textContent !== text) slot.textContent = text;
      item.hidden = !ok;
    });
  }
  let fillQueued = false;
  new MutationObserver(() => {
    if (fillQueued) return;
    fillQueued = true;
    requestAnimationFrame(() => {
      fillQueued = false;
      if (document.querySelector('[data-g-ship-one]')) fillShipOne();
      if (document.querySelector('[data-g-ship-quote], [data-g-ship-save], [data-g-bundle-total]')) fillQuotes();
      if (document.querySelector('[data-g-countdown]')) tickCountdown();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // ---------- notices ----------
  function showNotice(kind) {
    root.classList.toggle('g-notice-claimed', kind === 'claimed');
    root.classList.toggle('g-notice-ended', kind === 'ended');
    try {
      if (kind) sessionStorage.setItem(NOTICE_KEY, kind);
      else sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {}
  }
  try {
    const kind = sessionStorage.getItem(NOTICE_KEY);
    if (kind) showNotice(kind);
  } catch (e) {}

  // ---------- bag ----------
  const getCart = () => fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const refreshViews = () => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'g-offer' });
    }
  };

  // Puts exactly the current state's codes on the bag. Returns 'claimed' / 'ended' when a promise the bag carried
  // is no longer available, otherwise null.
  async function syncCart(result) {
    if (cfg.designMode || !result) return null;
    const cart = await getCart();
    const current = (cart.discount_codes || []).map((item) => item.code);
    const others = current.filter((code) => !ours.has(up(code)));
    const want = result.view === 'a' ? giveCodes : result.view === 'b' ? launchCodes : [];
    const next = [...others, ...want];
    const same = next.length === current.length && next.every((code) => current.some((c) => up(c) === up(code)));
    if (same) return null;

    const had = (code) => code && current.some((c) => up(c) === up(code));
    let lost = null;
    if (cart.item_count > 0) {
      if (cfg.giveaway && had(cfg.giveaway.code) && result.state !== 'a') lost = 'claimed';
      else if (cfg.launch && had(cfg.launch.code) && result.state === 'c') lost = 'ended';
    }
    await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(',') }),
    });
    if (lost) showNotice(lost);
    if (cart.item_count > 0) refreshViews();
    return lost;
  }

  // Real bag, priced with a US address: confirms (or not) that shipping is free for exactly these lines and codes.
  let verifySeq = 0;
  async function verifyBagShipping(cart) {
    const seq = ++verifySeq;
    const lines = (cart.items || []).map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
    }));
    if (!lines.length || !isUS) {
      root.classList.remove('g-cart-ship-free');
      return;
    }
    const codes = (cart.discount_codes || []).map((item) => item.code);
    const result = await simulate(lines, codes, cfg.country, true).catch(() => null);
    if (seq !== verifySeq) return;
    root.classList.toggle('g-cart-ship-free', Boolean(result && result.shipKnown && result.shipFree));
  }

  async function checkBag(freshIfStale) {
    if (cfg.designMode) return;
    try {
      let cart = await getCart();
      if (!cart.item_count) {
        showNotice(null);
        root.classList.remove('g-cart-ship-free');
        return;
      }
      const mainCodes = [cfg.giveaway?.code, cfg.launch?.code].filter(Boolean).map(up);
      const stale = (cart.discount_codes || []).some((item) => mainCodes.includes(up(item.code)) && item.applicable === false);
      if (stale && freshIfStale) {
        const result = await resolveState(true);
        apply(result);
        if (await syncCart(result)) cart = await getCart();
      }
      await verifyBagShipping(cart);
    } catch (e) {}
  }

  // ---------- checkout ----------
  function isCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (submitter) return submitter.name === 'checkout';
    return Boolean(event.target.querySelector && event.target.querySelector('[name="checkout"]'));
  }

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (cfg.designMode || !(form instanceof HTMLFormElement) || !isCheckoutSubmit(event)) return;
      if (form.dataset.gOfferChecked === '1') {
        delete form.dataset.gOfferChecked;
        showNotice(null);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const submitter = event.submitter;
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      let lost = null;
      try {
        const result = await withTimeout(resolveState(true), 6000);
        if (result) {
          apply(result);
          lost = await syncCart(result);
        }
      } catch (e) {}
      if (submitter) submitter.removeAttribute('aria-busy');
      if (lost) {
        const notice = document.querySelector(`[data-g-notice="${lost}"]`);
        if (notice) notice.scrollIntoView({ block: 'nearest' });
        return;
      }
      form.dataset.gOfferChecked = '1';
      if (submitter && submitter.form === form) form.requestSubmit(submitter);
      else form.requestSubmit();
    },
    true
  );

  // ---------- start ----------
  async function run() {
    const result = await resolveState(false);
    apply(result);
    try {
      await syncCart(result);
    } catch (e) {}
    checkBag(true);
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      let timer;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        clearTimeout(timer);
        timer = setTimeout(() => checkBag(event?.source !== 'g-offer'), 400);
      });
    }
  });

  run();
})();
